// Helper minimo pra empurrar eventos pro dataLayer do Google Tag Manager.
// O container e instalado no layout raiz (src/app/layout.tsx) atras da env
// NEXT_PUBLIC_GTM_ID. Quando a env nao esta setada, o container nao carrega e
// os pushes viram no-op inofensivo (dataLayer so acumula, ninguem consome).
//
// Como o app e servido sob clubesbrazil.com via rewrite do Vercel, e o mesmo
// container da landing, os eventos daqui fecham o funil:
//   landing (pageview + clique no CTA) -> atendimento_pagina -> whatsapp_redirect
//   -> nps_respondido.

import { waLinkSeguro } from '@/lib/wa-link-seguro'

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

/**
 * Empurra um evento nomeado pro dataLayer. Seguro pra chamar em qualquer lugar
 * (checa window) - no SSR vira no-op.
 */
export function gtmEvent(event: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...params })
}

/**
 * Empurra um evento e SÓ navega depois que o GTM confirma o disparo das tags
 * (ou depois de `timeoutMs`, o que vier primeiro). Sem isso, um
 * `window.location.href` logo após o push descarrega a página antes do GA4
 * mandar o hit e a conversão se perde — foi o que acontecia no whatsapp_redirect.
 *
 * A navegação é GARANTIDA dentro de `timeoutMs` mesmo se o GTM não estiver
 * carregado (env ausente) ou nunca chamar o callback — o redirect do lead pro
 * WhatsApp nunca pode ficar preso por causa de tracking.
 *
 * Só navega para destino WhatsApp validado por `waLinkSeguro`. A URL chega da
 * query string ou de JSON, e um `javascript:` aqui rodaria na sessão de quem
 * abriu a página. Destino inválido: registra o evento e não navega.
 */
export function gtmEventThenNavigate(
  event: string,
  params: Record<string, unknown>,
  url: string,
  timeoutMs = 1000
): void {
  if (typeof window === 'undefined') return
  const destino = waLinkSeguro(url)
  if (!destino) {
    gtmEvent(event, { ...params, destino_invalido: true })
    return
  }
  let navegou = false
  const irPara = () => {
    if (navegou) return
    navegou = true
    window.location.href = destino
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event,
    ...params,
    eventCallback: irPara,
    eventTimeout: timeoutMs,
  })
  // Rede de seguranca: se o GTM nao estiver carregado, eventCallback nunca e
  // chamado — navega assim mesmo ao fim do timeout.
  setTimeout(irPara, timeoutMs)
}
